import type {Extension, ValidatorMap, ExtensionAPI, DalItem, DmApis} from '@wix/document-manager-core'
import type {Pointer, CompRef, BindingsObject} from '@wix/document-services-types'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {StructureExtensionAPI} from '../structure'
import {ReportableError} from '@wix/document-manager-utils'
import {DATA_TYPES} from '../../constants/constants'
import _ from 'lodash'

const BINDINGS_NAMESPACE = DATA_TYPES.bindings

type BindingsExtensionAPI = ExtensionAPI & {
    bindings: BindingsObject
}

const throwBindingsError = (method: string, message: string): never => {
    throw new ReportableError({errorType: 'BindingsError', message: `bindings.${method}: ${message}`})
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, pointers}: DmApis): BindingsExtensionAPI => {
        const {dataModel} = extensionAPI as DataModelExtensionAPI & StructureExtensionAPI

        const validateCompRef = (method: string, compRef: CompRef): void => {
            if (!compRef) {
                throwBindingsError(
                    method,
                    'componentPointer is required. Pass the pointer of the component you want to attach the context to'
                )
            }
        }

        const validateContextType = (method: string, contextType: string): void => {
            if (!contextType) {
                throwBindingsError(
                    method,
                    'contextType is required. Pass the name of the context you want to attach to the component'
                )
            }
            if (!_.isString(contextType)) {
                throwBindingsError(
                    method,
                    'contextType must be a string. Pass the name of the context you want to attach to the component'
                )
            }
        }

        const validateComponentExists = (method: string, {id, type}: CompRef): void => {
            if (!dataModel.getItem(id, type)) {
                throwBindingsError(method, `Component not found: ${id}`)
            }
        }

        const getBindingsListFromCompRef = (compRef: CompRef): DalItem | undefined => {
            return dataModel.components.getItem(compRef, BINDINGS_NAMESPACE)
        }

        const getPageId = (compRef: CompRef): string => {
            const pageId = pointers.structure.getPageOfComponent(compRef)?.id
            if (!pageId) {
                throwBindingsError('getPageId', `No pageId found for component: ${compRef.id}`)
            }
            return pageId
        }

        const bind = (
            compRef: CompRef,
            contextType: string,
            bindingsMap: Record<string, string>,
            replace: boolean
        ): void => {
            validateCompRef('bind', compRef)
            validateContextType('bind', contextType)
            validateComponentExists('bind', compRef)

            const existingCompBindingsList = getBindingsListFromCompRef(compRef)

            if (!existingCompBindingsList) {
                dataModel.components.addItem(compRef, BINDINGS_NAMESPACE, {
                    type: 'BindingsList',
                    values: [
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const bindingConfigs = existingCompBindingsList?.values ?? []

            const configAlreadyDefined = _.find(
                bindingConfigs,
                (config: DalItem) => config.contextProvider === contextType
            )

            if (!configAlreadyDefined) {
                dataModel.components.addItem(compRef, BINDINGS_NAMESPACE, {
                    ...existingCompBindingsList,
                    values: [
                        ...bindingConfigs,
                        {
                            type: 'ContextBindingConfig',
                            contextProvider: contextType,
                            properties: bindingsMap
                        }
                    ]
                })
                return
            }

            const updatedConfig = {
                ...configAlreadyDefined,
                properties: replace ? bindingsMap : {...configAlreadyDefined.properties, ...bindingsMap}
            }

            const updatedBindingConfigs = bindingConfigs.map((config: DalItem) =>
                config.id === updatedConfig.id ? updatedConfig : config
            )

            dataModel.components.addItem(compRef, BINDINGS_NAMESPACE, {
                ...existingCompBindingsList,
                values: updatedBindingConfigs
            })
        }

        const unbindProperties = (componentPointer: Pointer, contextType: string, properties: string[]): void => {
            validateCompRef('unbindProperties', componentPointer as CompRef)
            validateContextType('unbindProperties', contextType)
            validateComponentExists('unbindProperties', componentPointer as CompRef)

            const pageId = getPageId(componentPointer as CompRef)

            const existingCompBindingsList = getBindingsListFromCompRef(componentPointer as CompRef)

            if (!existingCompBindingsList) {
                throwBindingsError(
                    'unbindProperties',
                    `This component does not have bindings, id:${componentPointer.id}`
                )
            }

            const existingContextBindingConfigs = existingCompBindingsList?.values ?? []

            const contextConfigAlreadyDefined = _.find(
                existingContextBindingConfigs,
                (config: DalItem) => config.contextProvider === contextType
            )

            if (!contextConfigAlreadyDefined) {
                throwBindingsError('unbindProperties', `Context not found: ${contextType}`)
            }

            const newProperties = _.omit(contextConfigAlreadyDefined!.properties, properties)
            const updatedConfig = {
                ...contextConfigAlreadyDefined,
                properties: newProperties
            }
            dataModel.addItem(updatedConfig, BINDINGS_NAMESPACE, pageId, contextConfigAlreadyDefined!.id)
        }

        const getBoundProperties = (componentPointer: Pointer): Record<string, Record<string, string>> => {
            validateCompRef('getBoundProperties', componentPointer as CompRef)
            validateComponentExists('getBoundProperties', componentPointer as CompRef)

            const existingCompBindingsList = getBindingsListFromCompRef(componentPointer as CompRef)
            if (!existingCompBindingsList?.values || existingCompBindingsList.values.length === 0) {
                return {}
            }

            const bindings: Record<string, Record<string, string>> = {}

            for (const config of existingCompBindingsList.values) {
                bindings[(config as DalItem).contextProvider] = (config as DalItem).properties
            }

            return bindings
        }

        return {
            bindings: {
                bind: (componentPointer: CompRef, contextType: string, bindingsMap: Record<string, string>): void =>
                    bind(componentPointer, contextType, bindingsMap, false),
                bindAll: (componentPointer: CompRef, contextType: string, bindingsMap: Record<string, string>): void =>
                    bind(componentPointer, contextType, bindingsMap, true),
                unbindProperties: (componentPointer: CompRef, contextType: string, properties: string[]): void =>
                    unbindProperties(componentPointer, contextType, properties),
                getBoundProperties: (componentPointer: CompRef): Record<string, Record<string, string>> =>
                    getBoundProperties(componentPointer)
            }
        }
    }

    const createValidator = (): ValidatorMap => {
        return {
            // No bindings on a component if the context is not above in the tree
            // No conflicting keys between bindings
            // No circular dependency
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): BindingsExtensionAPI => {
        const {bindings: bindingsAPI} = extensionAPI as BindingsExtensionAPI
        return {
            bindings: {
                bind: bindingsAPI.bind,
                bindAll: bindingsAPI.bindAll,
                unbindProperties: bindingsAPI.unbindProperties,
                getBoundProperties: bindingsAPI.getBoundProperties
            }
        }
    }

    return {
        dependencies: new Set(['contexts', 'dataModel', 'structure']),
        name: 'bindings',
        createExtensionAPI,
        createValidator,
        createPublicAPI
    }
}

export {createExtension}
